import React, { useEffect, useState } from "react";
import { Edit, PillIcon, Trash2, XIcon } from "lucide-react";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as medicationActions from "src/redux/slices/medicationSlice";
import { toast } from "sonner";
import Loader from "src/components/common/Loader";
import ConfirmationModal from "../common/ConfirmationModal";
import AddMedicationModal from "./AddMedicationModal";

interface MedicationListProps {
  refresh: boolean;
  setShowEditMedicationModal: (show: boolean) => void;
  setMedication: (medication: any) => void;
}

const MedicationList: React.FC<MedicationListProps> = ({
  refresh,
  setShowEditMedicationModal,
  setMedication,
}) => {
  const dispatch = useAppDispatch();
  const { medicationsList } = useAppSelector((state) => state.medication);
  const [showEditModal, setShowEditModal] = useState(false);
  useEffect(() => {
    getMedicationList();
  }, []);

  useEffect(() => {
    if (refresh) {
      getMedicationList();
    }
  }, [refresh]);

  const getMedicationList = () => {
    new Promise((resolve, reject) => {
      dispatch(
        medicationActions.getMedicationListRequest({
          resolve,
          reject,
        })
      );
    });
  };

  const handleEditMedication = (medication: any) => {
    setShowEditMedicationModal(true);
    new Promise((resolve, reject) => {
      dispatch(
        medicationActions.getMedicationByIdRequest({
          payload: { medication_id: medication._id },
          resolve,
          reject,
        })
      );
    }).then((res: any) => {
      setMedication(res.data);
    });
    // setMedication(medication);
  };

  return (
    <div className="w-full relative">
      <div className="relative w-full space-y-4">
        <div className="relative w-full">
          <h6 className="text-sm font-medium text-gray-700">
            Existing Medications:
          </h6>
        </div>
        {medicationsList?.isLoading ? (
          <div className="w-full flex items-center justify-center">
            <Loader size="lg" />
          </div>
        ) : (
          <div className="relative w-full space-y-2">
            {medicationsList?.data?.length > 0 ? (
              medicationsList?.data?.map((medication: any, index: number) => (
                <MedicationItem
                  key={index.toString()}
                  medication={medication}
                  handleEditMedication={handleEditMedication}
                  getMedicationList={getMedicationList}
                />
              ))
            ) : (
              <div className="w-full bg-gray-100 p-2 rounded-md">
                <span className="text-sm">No medications found</span>
              </div>
            )}
          </div>
        )}
      </div>
      {showEditModal && (
        <AddMedicationModal
          onSuccess={() => {
            setShowEditModal(false);
            getMedicationList();
          }}
          handleClose={() => setShowEditModal(false)}
        />
      )}
    </div>
  );
};

interface MedicationItemProps {
  medication: any;
  handleEditMedication: (medication: any) => void;
  getMedicationList: () => void;
}

const MedicationItem: React.FC<MedicationItemProps> = ({
  medication,
  handleEditMedication,
  getMedicationList,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const dispatch = useAppDispatch();
  const { deleteMedication, medicationLog } = useAppSelector(
    (state) => state.medication
  );

  const handleDeleteMedication = (medication: any) => {
    const payload = {
      medication_id: medication?._id,
    };
    new Promise((resolve, reject) => {
      dispatch(
        medicationActions.deleteMedicationRequest({
          payload,
          resolve,
          reject,
        })
      );
    }).then((res) => {
      setShowDeleteModal(false);
      toast.success("Medication deleted successfully");
      getMedicationList();
    });
  };

  return (
    <div className="w-full bg-gray-100 p-2 rounded-md">
      <div className="w-full flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[var(--icon-bg-primary)] rounded-md flex items-center justify-center">
            <PillIcon className="w-4 h-4 text-[var(--app-primary)]" />
          </div>
          <span className="text-sm">{medication?.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="p-1.5 rounded-md active:text-black active:bg-gray-200"
            onClick={() => handleEditMedication(medication)}
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            type="button"
            className="p-1.5 rounded-md text-red-500 active:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => setShowDeleteModal(true)}
            disabled={medication?._id === medicationLog?.data?.medication_id}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {showDeleteModal && (
        <ConfirmationModal
          show={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          title="Delete Medication"
          description="Are you sure you want to delete this medication?"
          onConfirm={() => handleDeleteMedication(medication)}
          isLoading={deleteMedication?.isLoading}
        />
      )}
      {showEditModal && (
        <AddMedicationModal
          onSuccess={() => {
            setShowEditModal(false);
            getMedicationList();
          }}
          handleClose={() => setShowEditModal(false)}
        />
      )}
    </div>
  );
};

export default MedicationList;
